import React, { useEffect, useMemo } from 'react';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { FloatingLabelInput } from '@/components/ui/floatinglable/floating';
import { LoadingButton } from '@/components/ui/common/loading-button';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/components/ui/use-toast';
import { RootState } from '@/redux/store';
import { useGetSalesTaxListQuery } from '@/services/salesTaxApi';
import {
    useCreateIncomeCategoryMutation,
    useUpdateIncomeCategoryMutation,
} from '@/services/incomeCategoryApi';
import { IncomeCategory } from '@/types/system-setting/income-category';
import { RevenueType, Status } from '@/types/enums';
import { ErrorType, FormProps } from '@/types';

interface IncomeCategoryFormData {
    id?: number;
    name: string;
    revenue_type: RevenueType | undefined;
    status: Status;
    org_id: number;
}

const IncomeCategoryForm = ({
    isOpen,
    setOpen,
    data,
    setData,
    action,
    setAction,
    refetch,
}: FormProps<IncomeCategory>) => {
    const { toast } = useToast();
    const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;

    const getInitialValues = () => ({
        name: '',
        revenue_type: undefined,
        status: Status.Active,
        org_id: orgId,
    });

    const { data: salesTaxData } = useGetSalesTaxListQuery(orgId);

    const salesTaxOptions = useMemo(() => {
        return Array.isArray(salesTaxData)
            ? salesTaxData.map((saleTax) => ({
                label: `${saleTax.name} (${saleTax.percentage}%)`,
                value: saleTax.id?.toString() || "",
            }))
            : [];
    }, [salesTaxData]);
    const [createIncomeCategory, { isLoading: createLoading }] = useCreateIncomeCategoryMutation();
    const [updateIncomeCategory, { isLoading: updateLoading }] = useUpdateIncomeCategoryMutation();

    const form = useForm<IncomeCategoryFormData>({
        mode: "all",
        defaultValues: getInitialValues(),
    });

    const {
        handleSubmit,
        reset,
        formState: { isSubmitting },
    } = form;

    useEffect(() => {
        if (data && action === 'edit') {
            reset({
                id: data.id,
                name: data.name,
                revenue_type: data.revenue_type,
                status: data.status,
                org_id: orgId,
            });
        } else {
            // Always reset to initial values when switching to add mode
            reset(getInitialValues());
        }
    }, [data, action, reset, orgId]);

    // Additional effect to handle action changes specifically
    useEffect(() => {
        if (action === 'add') {
            reset(getInitialValues());
        }
    }, [action, reset, orgId]);

    const onSubmit = async (formData: IncomeCategoryFormData) => {
        const payload: IncomeCategory = {
            ...formData,
            name: formData.name.toLowerCase(),
            revenue_type: formData.revenue_type!,
        };

        try {
            let resp;
            if (action === 'add') {
                resp = await createIncomeCategory(payload);
                if (!resp.error) {
                    refetch();
                    toast({
                        variant: 'success',
                        title: 'Income category added successfully.',
                    });
                    handleClose();
                } else {
                    const error = resp.error as ErrorType;
                    throw new Error(error.data?.detail || error.data?.message || 'Something went wrong.');
                }
            } else {
                resp = await updateIncomeCategory(payload);
                if (!resp.error) {
                    refetch();
                    toast({
                        variant: 'success',
                        title: 'Income category updated successfully.',
                    });
                    handleClose();
                } else {
                    const error = resp.error as ErrorType;
                    throw new Error(error.data?.detail || error.data?.message || 'Something went wrong.');
                }
            }
        } catch (error) {
            console.error('Error', { error });
            toast({
                variant: 'destructive',
                title: 'Error in form Submission',
                description: error instanceof Error ? error.message : 'Something went wrong.',
            });
        }
    };

    const handleClose = () => {
        // Force reset to initial values with undefined for select fields
        reset(getInitialValues(), {
            keepDefaultValues: false
        });
        setAction("add");
        setData(undefined);
        setOpen(false);
    };

    return (
        <Sheet open={isOpen} onOpenChange={handleClose}>
            <SheetContent className="w-full sm:max-w-md px-4 sm:px-6">
                <SheetHeader>
                    <SheetTitle className="text-base sm:text-lg">
                        {action === 'add' ? 'Create' : 'Edit'} Income Category
                    </SheetTitle>
                    <SheetDescription>
                        <Separator className="h-[1px] font-thin rounded-full" />

                        <FormProvider {...form}>
                            <form
                                onSubmit={handleSubmit(onSubmit)}
                                className="flex flex-col py-4 gap-4"
                            >
                                <Controller
                                    name="name"
                                    control={form.control}
                                    rules={{
                                        required: 'Required',
                                        maxLength: {
                                            value: 40,
                                            message: 'Should be 40 characters or less',
                                        },
                                        pattern: {
                                            value: /^[a-zA-Z]+[-'s]?[a-zA-Z ]+$/,
                                            message: 'Name should contain only alphabets',
                                        },
                                    }}
                                    render={({ field, fieldState: { error } }) => (
                                        <div className="space-y-1">
                                            <FloatingLabelInput
                                                id="name"
                                                label="Category Name"
                                                text="*"
                                                className="capitalize"
                                                {...field}
                                            />
                                            {error && (
                                                <p className="text-xs text-red-500 mt-5">{error.message}</p>
                                            )}
                                        </div>
                                    )}
                                />

                                <Controller
                                    name="revenue_type"
                                    control={form.control}
                                    rules={{
                                        required: 'Required',
                                    }}
                                    render={({ field, fieldState: { error } }) => (
                                        <div className="space-y-1">
                                            <Select
                                                value={field.value || ''}
                                                onValueChange={(value) => {
                                                    if (value && value !== '') {
                                                        field.onChange(value as RevenueType);
                                                    } else {
                                                        field.onChange(undefined);
                                                    }
                                                }}
                                            >
                                                <SelectTrigger
                                                    className="capitalize"
                                                    floatingLabel="Revenue"
                                                    text="*"
                                                >
                                                    <SelectValue placeholder="Select Revenue" />
                                                </SelectTrigger>
                                                <SelectContent className="capitalize">
                                                    <SelectItem value={RevenueType.Income}>Income</SelectItem>
                                                    <SelectItem value={RevenueType.Donation}>Donation</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            {error && (
                                                <p className="text-xs text-red-500 mt-5">{error.message}</p>
                                            )}
                                        </div>
                                    )}
                                />

                                <LoadingButton
                                    type="submit"
                                    className="bg-primary font-semibold text-black gap-1"
                                    loading={isSubmitting || createLoading || updateLoading}
                                >
                                    {!isSubmitting && !createLoading && !updateLoading && (
                                        <i className="fa-regular fa-floppy-disk text-base px-1" />
                                    )}
                                    Save
                                </LoadingButton>
                            </form>
                        </FormProvider>
                    </SheetDescription>
                </SheetHeader>
            </SheetContent>
        </Sheet>
    );
};

export default IncomeCategoryForm;